// frontend/src/components/AppHeader.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import GlobalSearch from "./GlobalSearch";
import NotificationsIcon from "./NotificationsIcon";
import { isIOS } from "../utils/platform";
import { DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";
import { Preferences } from "@capacitor/preferences";

import { CaretDown } from "phosphor-react";

const AppHeader = ({ user }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // viewport width -> compact mode
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isCompact = isIOS || isNarrow;

  const isActive = (path) => pathname.startsWith(path);
  const navLink =
    "relative hidden items-center transition-colors hover:text-blue-400 sm:inline-flex";

  const active =
    "text-blue-600 font-semibold after:absolute after:-bottom-2 after:left-0 after:h-[3px] after:w-full after:bg-blue-600";

  const items = useMemo(
    () => [
      { label: "home", value: "/home", match: "/home" },
      { label: "circles", value: "/groups", match: "/groups" },
    ],
    []
  );

  const current = useMemo(
    () => items.find((i) => pathname.startsWith(i.match)) || items[0],
    [items, pathname]
  );

  // mobile nav dropdown
  const [open, setOpen] = useState(false);
  const ddRef = useRef(null);
  useEffect(() => {
    const onDocPointer = (e) => {
      if (!ddRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, []);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  // --- profile dropdown ---
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const onDocPointer = (e) => {
      if (!profileRef.current?.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, []);

  const handleLogout = async () => {
    try {
      // clear tokens in web storage
      localStorage.removeItem("token");
      localStorage.removeItem("user_id");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user_id");

      // best-effort clear native token
      try {
        await Preferences.remove({ key: "auth_token" });
      } catch {
        // ignore if not available
      }
    } finally {
      setProfileMenuOpen(false);
      navigate("/login");
    }
  };

  const goToProfile = () => {
    if (!user) return;
    setProfileMenuOpen(false);
    navigate(`/profile/${user.id}`);
  };

  const goToSettings = () => {
    setProfileMenuOpen(false);
    navigate("/settings"); // adjust if your route is different
  };

  return (
    // Sticky header; NO safe-area padding here (parent .app-header handles it)
    <nav className="relative z-40 bg-transparent pt-2 backdrop-blur sm:bg-white">
    {/* Active tab underline on the bottom edge of the header */}
    <div
      className={`
        pointer-events-none absolute bottom-0 hidden h-[3px] rounded-t-full
        bg-blue-600 transition-all duration-300 ease-out sm:block
        ${
          isActive("/home")
            ? "left-[var(--home-underline-left)] w-[var(--home-underline-width)]"
            : isActive("/groups")
            ? "left-[var(--groups-underline-left)] w-[var(--groups-underline-width)]"
            : "w-0 opacity-0"
        }
      `}
    />

      <div className="mx-auto flex h-12 max-w-7xl items-center justify-between px-6 md:px-10 xl:px-16">
        {/* LEFT cluster */}
        <div className="flex items-center gap-4 sm:gap-10">
          {/* Logo -> Home */}
          <Link
            to="/home?scope=following"
            className={`logo-font pl-2 sm:pl-4 whitespace-nowrap md:hover:opacity-80 focus:outline-none ${
              isCompact ? "text-[1.7rem]" : "text-[1.25rem] sm:text-[1.8rem]"
            }`}
            aria-label="Go to Home (Following)"
          >
            third place
          </Link>

          <div className="dm-font text-black font-black tracking-[-0.06em] flex items-center gap-3 sm:gap-7 text-[1.05rem] sm:text-[1.3rem]">
            <div className="instrument-font font-normal tracking-normal text-base">
              <GlobalSearch />
            </div>

            {/* Desktop nav items */}
            <Link
              to="/home"
              className={`${navLink} ${isActive("/home") ? active : ""}`}
            >
              home
            </Link>

            <Link
              to="/groups"
              className={`${navLink} ${isActive("/groups") ? active : ""}`}
            >
              circles
            </Link>

            {/* Mobile dropdown (hidden in compact mode) */}
            {!isCompact && (
              <div className="relative sm:hidden" ref={ddRef}>
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={open}
                  onClick={() => setOpen((v) => !v)}
                  className="px-2 pr-8 rounded-md border border-gray-200 bg-white shadow-sm focus:outline-none relative h-8"
                >
                  {current.label}
                  <CaretDown
                    size={16}
                    weight="bold"
                    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                  />
                </button>

                {open && (
                  <ul
                    role="listbox"
                    className="absolute left-0 mt-2 w-40 rounded-xl bg-white shadow-lg ring-1 ring-black/5 overflow-hidden"
                  >
                    {items.map((it) => {
                      const selected = pathname.startsWith(it.match);
                      return (
                        <li key={it.value} role="option" aria-selected={selected}>
                          <button
                            type="button"
                            onClick={() => go(it.value)}
                            className={`w-full text-left px-3 py-2 text-[1rem] ${
                              selected
                                ? "bg-gray-50 font-semibold"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            {it.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT cluster */}
        <div className="flex items-center gap-3 sm:gap-4">
          <NotificationsIcon className="text-blue-500 md:mr-4" />

          {user ? (
            <div className="relative" ref={profileRef}>
              <button
                type="button"
                onClick={() => setProfileMenuOpen((v) => !v)}
                className="flex items-center gap-1 focus:outline-none"
              >
                <img
                  src={user.profile_pic_url || DEFAULT_PROFILE_IMAGE}
                  alt="avatar"
                  className="md:mb-2 w-8 h-8 rounded-full cursor-pointer object-cover"
                />
                <CaretDown
                  size={16}
                  weight="bold"
                  className="hidden sm:block text-gray-500 md:mb-2"
                />
              </button>

              {profileMenuOpen && (
                <div className="absolute right-0 w-36 bg-white rounded-2xl shadow-[0_8px_0px_rgba(37,99,235,0.96)] ring-1 ring-black/99 py-1 text-sm z-50">
                  <button
                    type="button"
                    onClick={goToProfile}
                    className="w-full text-left px-4 py-2 hover:font-semibold" 
                  >
                    My Profile
                  </button>
                  <button
                    type="button"
                    onClick={goToSettings}
                    className="w-full text-left px-4 py-2 hover:font-semibold"
                  >
                    Settings
                  </button>
                  {/*<div className="my-1 border-t border-gray-100" /> */}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-red-600 hover:font-semibold"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200" />
          )}
        </div>
      </div>
    </nav>
  );
};

export default AppHeader;
